import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Search,
  Menu,
  X,
  User,
  Heart,
  PlusCircle,
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  Globe,
  HelpCircle,
  Check,
  CheckCircle,
  FileCheck,
  Target,
  DollarSign,
  Shield,
  Sun,
  Moon,
  MessageCircle,
  Wallet,
  Filter,
  Loader2,
  BadgeCheck,
  FileText,
  Crown,
  LayoutDashboard,
  ArrowRight,
} from 'lucide-react';
import { categories } from '../../data/mock';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import apiClient, { campaignAPI, donationAPI } from '../../services/api';
import notificationSound from '../../utils/notificationSound';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [enquiryCount, setEnquiryCount] = useState(0);
  const [lastEnquiryCount, setLastEnquiryCount] = useState(0);
  const [showPreferencesDialog, setShowPreferencesDialog] = useState(false);
  const [showDonateDialog, setShowDonateDialog] = useState(false);
  const [donateLoading, setDonateLoading] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [donationForm, setDonationForm] = useState({
    campaignId: 'general',
    amount: '',
    customAmount: '',
    message: '',
    anonymous: false,
  });
  const [prefCategory, setPrefCategory] = useState('');
  const [prefSubcategory, setPrefSubcategory] = useState('');
  const [selectedPreferenceCategories, setSelectedPreferenceCategories] = useState([]);
  const [preferenceNotificationsEnabled, setPreferenceNotificationsEnabled] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const { user, isAuthenticated, logout, updateCampaignPreferences } = useAuth();
  const { language, setLanguage, t, currentLanguage, languages, languagesByRegion } = useLanguage();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  // Fetch campaigns for quick donate
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const data = await campaignAPI.list({ status: 'active', limit: 20 });
        setAvailableCampaigns(data || []);
      } catch (err) {
        console.log('Failed to fetch campaigns for donate:', err);
      }
    };
    if (showDonateDialog) {
      fetchCampaigns();
    }
  }, [showDonateDialog]);

  // Initialize preferences from user data
  useEffect(() => {
    if (user) {
      setPrefCategory(user.preferred_category || '');
      setPrefSubcategory(user.preferred_subcategory || '');
      setSelectedPreferenceCategories(user.campaign_preferences || []);
      setPreferenceNotificationsEnabled(user.campaign_preference_notifications !== false);
    }
  }, [user]);

  // Fetch campaign notification preferences
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await apiClient.get('/auth/campaign-notification-preferences');
        setSelectedPreferenceCategories(response.data.campaign_preferences || []);
        setPreferenceNotificationsEnabled(response.data.campaign_preference_notifications !== false);
      } catch (err) {
        console.log('Failed to fetch campaign preferences:', err);
      }
    };
    fetchNotificationPreferences();
  }, [isAuthenticated]);

  // Get subcategories for selected preference category
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));
  const selectedPrefCategoryData = categories.find(c => c.id === prefCategory);
  const prefSubcategories = selectedPrefCategoryData?.subcategories 
    ? [...selectedPrefCategoryData.subcategories].sort() 
    : [];

  const togglePreferenceCategory = (categoryId) => {
    setSelectedPreferenceCategories(prev => {
      const isSelected = prev.includes(categoryId);
      return isSelected
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
    });
  };

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      // Save both the old browsing preferences and new notification preferences
      await updateCampaignPreferences(prefCategory || null, prefSubcategory || null);
      
      // Save multiple category preferences for notifications
      await apiClient.put('/auth/campaign-notification-preferences', {
        campaign_preferences: selectedPreferenceCategories,
        campaign_preference_notifications: preferenceNotificationsEnabled,
      });
      
      setShowPreferencesDialog(false);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleClearPreferences = async () => {
    setSavingPrefs(true);
    try {
      await updateCampaignPreferences(null, null);
      await apiClient.put('/auth/campaign-notification-preferences', {
        campaign_preferences: [],
        campaign_preference_notifications: true,
      });
      setPrefCategory('');
      setPrefSubcategory('');
      setSelectedPreferenceCategories([]);
      setShowPreferencesDialog(false);
    } catch (error) {
      console.error('Failed to clear preferences:', error);
    } finally {
      setSavingPrefs(false);
    }
  };
  
  // Fetch notifications for all users
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user || !isAuthenticated) return;
      
      setNotifLoading(true);
      try {
        // For admin users, get admin-specific notifications
        if (['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user.role)) {
          try {
            const response = await apiClient.get('/admin/notifications');
            setNotifications(response.data.notifications || []);
          } catch {
            // Fallback - generate notifications from pending items
            const [kycRes, campaignsRes] = await Promise.all([
              apiClient.get('/admin/kyc/pending'),
              apiClient.get('/admin/campaigns/pending'),
            ]);
            const notifs = [];
            const pendingKYC = kycRes.data.pending_kyc || [];
            const pendingCampaigns = campaignsRes.data.pending_campaigns || [];
            
            pendingKYC.forEach(kyc => {
              notifs.push({
                id: `kyc-${kyc.id}`,
                type: 'kyc',
                title: 'KYC Review Pending',
                message: `${kyc.name} submitted documents for verification`,
                time: kyc.kyc_documents?.[0]?.uploaded_at,
                link: '/admin?tab=kyc',
                read: false,
              });
            });
            
            pendingCampaigns.forEach(campaign => {
              notifs.push({
                id: `campaign-${campaign.id}`,
                type: 'campaign',
                title: 'Campaign Pending Review',
                message: `"${campaign.title}" needs approval`,
                time: campaign.created_at,
                link: '/admin?tab=campaigns',
                read: false,
              });
            });
            
            setNotifications(notifs.slice(0, 10));
          }
        } else {
          // For regular users, fetch from notifications API
          const response = await apiClient.get('/notifications/', {
            params: { unread_only: false, limit: 10 }
          });
          // Filter out admin-only notifications (enquiry type) for regular users
          const userNotifications = (response.data.notifications || []).filter(
            n => n.type !== 'enquiry' && n.type !== 'kyc_submission'
          );
          setNotifications(userNotifications);
        }
      } catch (err) {
        console.log('Notifications fetch error:', err);
        setNotifications([]);
      } finally {
        setNotifLoading(false);
      }
    };
    
    if (isAuthenticated) {
      fetchNotifications();
    }
  }, [user, isAuthenticated]);

  // Poll for new enquiries (admin only) with sound notification
  useEffect(() => {
    const fetchEnquiryCount = async () => {
      if (!user || !isAuthenticated) return;
      if (!['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user.role)) return;
      
      try {
        const response = await apiClient.get('/enquiries/new-count');
        const newCount = response.data.count || 0;
        
        // Play sound if there are new enquiries
        if (newCount > lastEnquiryCount && lastEnquiryCount >= 0) {
          notificationSound.playEnquiryAlert();
        }
        
        setLastEnquiryCount(enquiryCount);
        setEnquiryCount(newCount);
      } catch (err) {
        // Silently fail - enquiry API might not be available
      }
    };
    
    if (isAuthenticated && user && ['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user.role)) {
      fetchEnquiryCount();
      // Poll every 30 seconds for new enquiries
      const interval = setInterval(fetchEnquiryCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isAuthenticated, lastEnquiryCount, enquiryCount]);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await apiClient.post(`/notifications/mark-read/${notificationId}`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.log('Mark read error:', err);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      await apiClient.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.log('Mark all read error:', err);
    }
  };

  // Count unread notifications (include enquiry count for admins)
  const unreadNotificationCount = notifications.filter(n => !n.read).length;
  const isAdmin = user && ['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user.role);
  const unreadCount = isAdmin ? unreadNotificationCount + enquiryCount : unreadNotificationCount;
  
  // Play sound when opening notifications panel with unread items
  const handleNotificationClick = () => {
    if (unreadCount > 0) {
      notificationSound.playBeep();
    }
  };
  
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'kyc': return <FileCheck className="w-4 h-4 text-teal-400" />;
      case 'campaign': return <Target className="w-4 h-4 text-emerald-400" />;
      case 'donation': return <DollarSign className="w-4 h-4 text-green-400" />;
      case 'preference_match': return <Heart className="w-4 h-4 text-pink-400" />;
      case 'update': return <Bell className="w-4 h-4 text-blue-400" />;
      case 'milestone': return <Target className="w-4 h-4 text-purple-400" />;
      case 'enquiry': return <MessageCircle className="w-4 h-4 text-amber-400" />;
      default: return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };
  
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const [searchingUser, setSearchingUser] = useState(false);
  const [searchResults, setSearchResults] = useState({ users: [], campaigns: [] });
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Autocomplete search as user types
  useEffect(() => {
    const searchAutocomplete = async () => {
      if (searchQuery.length < 2) {
        setSearchResults({ users: [], campaigns: [] });
        setShowSearchDropdown(false);
        return;
      }

      try {
        const response = await apiClient.get(`/profile/autocomplete?q=${encodeURIComponent(searchQuery)}&limit=5`);
        setSearchResults(response.data);
        setShowSearchDropdown(true);
      } catch (err) {
        console.error('Autocomplete error:', err);
        setSearchResults({ users: [], campaigns: [] });
      }
    };

    const debounce = setTimeout(searchAutocomplete, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    setShowSearchDropdown(false);

    // Check if searching for a 6-digit member ID
    if (/^\d{6}$/.test(query)) {
      setSearchingUser(true);
      try {
        const response = await apiClient.get(`/profile/search/${query}`);
        if (response.data && response.data.id) {
          navigate(`/profile/${response.data.id}`);
          setSearchQuery('');
          setSearchingUser(false);
          return;
        }
      } catch (err) {
        // User not found, continue with regular search
        console.log('No user found with ID:', query);
      }
      setSearchingUser(false);
    }

    // Regular campaign search
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const handleSelectUser = (user) => {
    setShowSearchDropdown(false);
    setSearchQuery('');
    navigate(`/profile/${user.id}`);
  };

  const handleSelectCampaign = (campaign) => {
    setShowSearchDropdown(false);
    setSearchQuery('');
    navigate(`/campaign/${campaign.id}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 group"
            onClick={(e) => {
              // If already on home page, just scroll to top
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                // Navigate and then scroll
                setTimeout(() => window.scrollTo(0, 0), 100);
              }
            }}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center transform group-hover:scale-105 transition-transform duration-200">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground hidden sm:block">FundTheHope</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-foreground hover:text-primary hover:bg-primary/10">
                  {t('discover')} <ChevronDown className="ml-1 w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                {categories.slice(0, 6).map((cat) => (
                  <DropdownMenuItem key={cat.id} asChild>
                    <Link to={`/discover?category=${cat.id}`} className="cursor-pointer">
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/discover" className="cursor-pointer font-medium text-emerald-600">
                    {t('viewAllCategories')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/how-it-works">
              <Button variant="ghost" className="text-foreground hover:text-primary hover:bg-primary/10">
                {t('howItWorks')}
              </Button>
            </Link>

            <Link to="/success-stories">
              <Button variant="ghost" className="text-foreground hover:text-primary hover:bg-primary/10">
                {t('successStories')}
              </Button>
            </Link>
          </nav>

          {/* Spacer to push right-side actions */}
          <div className="flex-1" />

          {/* Right Side Actions */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-1 px-2">
                  <span className="text-lg">{currentLanguage.flag}</span>
                  <span className="hidden sm:inline text-xs font-medium">{currentLanguage.code.toUpperCase()}</span>
                  <ChevronDown className="w-3 h-3 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  Select Language
                </div>
                <DropdownMenuSeparator />
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="cursor-pointer flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.nativeName}</span>
                    </span>
                    {language === lang.code && (
                      <Check className="w-4 h-4 text-emerald-500" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="relative"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-400 hover:text-yellow-300 transition-colors" />
              ) : (
                <Moon className="w-5 h-5 text-muted-foreground hover:text-foreground transition-colors" />
              )}
            </Button>

            {isAuthenticated ? (
              <>
                {/* Notifications Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative" onClick={handleNotificationClick}>
                      <Bell className="w-5 h-5 text-gray-600" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1 animate-pulse">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 flex flex-col" style={{ maxHeight: '400px' }}>
                    <div className="px-3 py-2 font-semibold text-sm border-b border-border flex items-center justify-between flex-shrink-0">
                      <span>Notifications</span>
                      {unreadCount > 0 && (
                        <button 
                          onClick={(e) => { e.preventDefault(); markAllNotificationsRead(); }}
                          className="text-xs text-emerald-500 hover:text-emerald-400"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    {/* Show enquiry alert for admins */}
                    {isAdmin && enquiryCount > 0 && (
                      <div 
                        onClick={() => navigate('/admin?tab=enquiries')}
                        className="px-3 py-3 bg-amber-500/10 border-b border-amber-500/20 cursor-pointer hover:bg-amber-500/20 transition-colors flex-shrink-0"
                      >
                        <div className="flex items-center gap-2 text-amber-600">
                          <MessageCircle className="w-4 h-4" />
                          <span className="text-sm font-semibold">{enquiryCount} New Enquir{enquiryCount === 1 ? 'y' : 'ies'}</span>
                        </div>
                        <p className="text-xs text-amber-600/80 mt-1">Click to view and respond</p>
                      </div>
                    )}
                    {/* Scrollable notifications area */}
                    <div className="overflow-y-auto flex-1" style={{ maxHeight: '280px' }}>
                      {notifLoading ? (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                          Loading...
                        </div>
                      ) : notifications.length === 0 && enquiryCount === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                          No notifications
                        </div>
                      ) : (
                        <>
                          {notifications.map((notif) => (
                            <DropdownMenuItem 
                              key={notif.id} 
                              onSelect={(e) => {
                                e.preventDefault();
                                if (!notif.read) markNotificationRead(notif.id);
                                navigate(notif.link || '/dashboard');
                              }}
                              className={`cursor-pointer p-3 flex items-start gap-3 ${!notif.read ? 'bg-emerald-500/5' : ''}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!notif.read ? 'bg-emerald-500/20' : 'bg-secondary'}`}>
                                {getNotificationIcon(notif.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notif.read ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>{notif.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{notif.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">{formatTimeAgo(notif.time || notif.created_at)}</p>
                              </div>
                              {!notif.read && <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-1.5" />}
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}
                    </div>
                    {/* Sticky View All button at bottom */}
                    <div className="border-t border-border flex-shrink-0">
                      <DropdownMenuItem 
                        onSelect={(e) => {
                          e.preventDefault();
                          // Admin users go to admin dashboard enquiries tab, regular users go to user dashboard notifications tab
                          if (isAdmin) {
                            navigate('/admin?tab=enquiries');
                          } else {
                            navigate('/settings?tab=notifications');
                          }
                        }} 
                        className="cursor-pointer justify-center text-emerald-500 py-3"
                      >
                        View All Notifications
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-2">
                      {user?.avatar ? (
                        <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-sm font-medium text-gray-900">{user?.name}</div>
                    <div className="px-2 pb-1.5 text-xs text-gray-500">{user?.email}</div>
                    {/* Role Badge for Admins */}
                    {['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user?.role) && (
                      <div className="px-2 pb-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          user?.role === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                          user?.role === 'platform_admin' ? 'bg-purple-500/20 text-purple-400' :
                          user?.role === 'reviewer' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {user?.role === 'super_admin' ? 'Super Admin' :
                           user?.role === 'platform_admin' ? 'Platform Admin' :
                           user?.role === 'reviewer' ? 'Reviewer' : 'Admin'}
                        </span>
                      </div>
                    )}
                    <DropdownMenuSeparator />
                    
                    {/* Admin-specific menu items */}
                    {['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user?.role) ? (
                      <>
                        {/* Super Admin Console - Only for super_admin */}
                        {user?.role === 'super_admin' && (
                          <DropdownMenuItem asChild>
                            <Link to="/super-admin" className="cursor-pointer">
                              <Crown className="mr-2 w-4 h-4 text-red-500" /> Super Admin Console
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {/* Platform Admin Dashboard - For platform_admin and super_admin */}
                        {['platform_admin', 'super_admin'].includes(user?.role) && (
                          <DropdownMenuItem asChild>
                            <Link to="/platform-admin" className="cursor-pointer">
                              <LayoutDashboard className="mr-2 w-4 h-4 text-purple-500" /> Platform Dashboard
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="cursor-pointer">
                            <Shield className="mr-2 w-4 h-4 text-emerald-500" /> Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin?tab=kyc" className="cursor-pointer">
                            <BadgeCheck className="mr-2 w-4 h-4" /> Verify Users
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin?tab=campaigns" className="cursor-pointer">
                            <FileText className="mr-2 w-4 h-4" /> Review Campaigns
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/messages" className="cursor-pointer">
                            <MessageCircle className="mr-2 w-4 h-4" /> Messages
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings" className="cursor-pointer">
                            <Settings className="mr-2 w-4 h-4" /> Settings
                          </Link>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        {/* Regular user menu items */}
                        <DropdownMenuItem asChild>
                          <Link to="/dashboard" className="cursor-pointer">
                            <User className="mr-2 w-4 h-4" /> My Dashboard
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/my-campaigns" className="cursor-pointer">
                            <Heart className="mr-2 w-4 h-4" /> My Campaigns
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/messages" className="cursor-pointer">
                            <MessageCircle className="mr-2 w-4 h-4" /> Messages
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/wallet" className="cursor-pointer">
                            <Wallet className="mr-2 w-4 h-4" /> Wallet
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings" className="cursor-pointer">
                            <Settings className="mr-2 w-4 h-4" /> Settings
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowPreferencesDialog(true)} className="cursor-pointer">
                          <Heart className="mr-2 w-4 h-4 text-emerald-500" /> 
                          <span>Campaign Preferences</span>
                          {(selectedPreferenceCategories.length > 0 || user?.campaign_preferences?.length > 0) && (
                            <span className="ml-auto text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">
                              {selectedPreferenceCategories.length || user?.campaign_preferences?.length}
                            </span>
                          )}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="cursor-pointer text-red-600">
                      <LogOut className="mr-2 w-4 h-4" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="hidden sm:inline-flex text-foreground hover:text-primary"
                  >
                    {t('signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    variant="outline"
                    className="hidden sm:inline-flex border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                  >
                    Sign Up
                  </Button>
                </Link>
              </>
            )}

            {/* Start Campaign Button - Hide for Admin users */}
            {!['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user?.role) && (
              <Link to="/create">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full px-5 shadow-md hover:shadow-lg transition-all duration-200">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{t('startCampaign')}</span>
                  <span className="sm:hidden">Start</span>
                </Button>
              </Link>
            )}

            {/* Support Us Button - Desktop */}
            <Link to="/support-us" className="hidden md:block">
              <Button variant="outline" size="sm" className="rounded-full border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-400">
                <Heart className="w-4 h-4 mr-1 fill-current" />
                Support Us
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-background border-t border-border py-4 px-4 space-y-3 animate-in slide-in-from-top duration-200">
          {/* Mobile Language Selector */}
          <div className="pb-3 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Language / Idioma / 语言:</span>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {/* Global */}
              <div className="flex gap-1 flex-wrap mb-2">
                {languagesByRegion.global?.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-2 py-1 rounded text-sm flex items-center gap-1 ${
                      language === lang.code
                        ? 'bg-emerald-500/20 text-emerald-500 font-medium'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span className="text-xs">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
              {/* EU Languages */}
              <p className="text-xs text-muted-foreground mb-1">Europe</p>
              <div className="flex gap-1 flex-wrap mb-2">
                {languagesByRegion.eu?.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                      language === lang.code
                        ? 'bg-emerald-500/20 text-emerald-500 font-medium'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
              {/* Asia Languages */}
              <p className="text-xs text-muted-foreground mb-1">Asia</p>
              <div className="flex gap-1 flex-wrap">
                {languagesByRegion.asia?.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                      language === lang.code
                        ? 'bg-emerald-500/20 text-emerald-500 font-medium'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={handleSearch} className="mb-4">
            <div className="relative">
              {searchingUser ? (
                <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
              ) : (
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              )}
              <Input
                type="text"
                placeholder="Search campaigns or User ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-secondary rounded-full"
              />
            </div>
          </form>

          <Link to="/discover" className="block py-2 text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
            {t('discover')}
          </Link>
          <Link to="/how-it-works" className="block py-2 text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
            {t('howItWorks')}
          </Link>
          <Link to="/success-stories" className="block py-2 text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
            {t('successStories')}
          </Link>
          <Link to="/help" className="block py-2 text-foreground hover:text-primary" onClick={() => setIsMenuOpen(false)}>
            <HelpCircle className="inline w-4 h-4 mr-2" /> Help Center
          </Link>
          <Link to="/support-us" className="block py-2 text-emerald-500 hover:text-emerald-400 font-medium" onClick={() => setIsMenuOpen(false)}>
            <Heart className="inline w-4 h-4 mr-2 fill-current" /> Support FundTheHope
          </Link>
          
          {/* Mobile Sign In / Sign Up - Only show when not authenticated */}
          {!isAuthenticated && (
            <div className="pt-3 mt-3 border-t border-border flex flex-col gap-2">
              <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                <Button variant="outline" className="w-full justify-center">
                  <User className="w-4 h-4 mr-2" />
                  {t('signIn')}
                </Button>
              </Link>
              <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full justify-center bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white">
                  Sign Up
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Campaign Preferences Dialog - Multi-Select */}
      <Dialog open={showPreferencesDialog} onOpenChange={setShowPreferencesDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-emerald-500" />
              Campaign Preferences
            </DialogTitle>
            <DialogDescription>
              Select campaign categories you are interested in. You will receive notifications when new campaigns matching your preferences are posted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Notifications Toggle */}
            <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg border border-border">
              <div>
                <p className="text-sm font-medium">Enable Notifications</p>
                <p className="text-xs text-muted-foreground">Get notified for new matching campaigns</p>
              </div>
              <button
                onClick={() => setPreferenceNotificationsEnabled(!preferenceNotificationsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferenceNotificationsEnabled ? 'bg-emerald-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferenceNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Multi-Select Categories */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Categories (Multiple)</label>
              <p className="text-xs text-muted-foreground mb-3">Click to select/deselect categories</p>
              <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto pr-1">
                {sortedCategories.map((cat) => {
                  const isSelected = selectedPreferenceCategories.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => togglePreferenceCategory(cat.id)}
                      disabled={!preferenceNotificationsEnabled}
                      className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-2 relative ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-500/10 text-foreground'
                          : 'border-border bg-secondary/50 text-muted-foreground hover:border-emerald-500/50'
                      } ${!preferenceNotificationsEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className="text-sm font-medium truncate flex-1">{cat.name}</span>
                      {isSelected && (
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Categories Summary */}
            {selectedPreferenceCategories.length > 0 && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-xs font-medium text-emerald-400 mb-2">
                  Selected ({selectedPreferenceCategories.length}):
                </p>
                <div className="flex flex-wrap gap-1">
                  {selectedPreferenceCategories.map(catId => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <span
                        key={catId}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded-full text-xs"
                      >
                        {cat.icon || '📌'} {cat.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Browsing Preference (Optional - Single Select) */}
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Default browsing filter (optional)</p>
              <Select 
                value={prefCategory || 'none'} 
                onValueChange={(value) => {
                  setPrefCategory(value === 'none' ? '' : value);
                  setPrefSubcategory('');
                }}
              >
                <SelectTrigger className="border border-border h-9">
                  <SelectValue placeholder="No default filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No default filter</SelectItem>
                  {sortedCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            {(selectedPreferenceCategories.length > 0 || user?.preferred_category) && (
              <Button 
                variant="outline" 
                onClick={handleClearPreferences}
                disabled={savingPrefs}
                className="text-red-400 hover:bg-red-500/10"
              >
                Clear All
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowPreferencesDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSavePreferences}
              disabled={savingPrefs}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              {savingPrefs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
